/**
 * Admin Reel Symbols Modal - View and configure all slot machine symbols
 * Features:
 * - Unified grid view of all 3D symbols
 * - Manual camera control (no auto-rotate) for position adjustment
 * - Animation support for symbols with GLB animations
 * - Expanded detail view with camera position values
 */

import { useState, Suspense, useMemo, useRef, useEffect, useCallback } from 'react';
import { X, Eye, Shield, Play, RotateCcw, Film, Move3D } from 'lucide-react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, Environment, Center } from '@react-three/drei';
import * as THREE from 'three';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { rumioDefaultTheme } from '../slot-machine/themes/rumio-default/theme.config';
import type { SymbolDefinition } from '../slot-machine/themes/types';

interface Props {
  onClose: () => void;
}

// Get all 3D symbols from theme config (exclude text3d symbols like letters)
const ALL_SYMBOLS = Object.values(rumioDefaultTheme.symbols).filter(
  (s) => s.renderMode !== 'text3d'
);

// =============================================================================
// Camera Position Tracker Component
// =============================================================================

interface CameraTrackerProps {
  onPositionChange: (position: { x: number; y: number; z: number }) => void;
}

function CameraTracker({ onPositionChange }: CameraTrackerProps) {
  const { camera } = useThree();

  useFrame(() => {
    onPositionChange({
      x: Math.round(camera.position.x * 100) / 100,
      y: Math.round(camera.position.y * 100) / 100,
      z: Math.round(camera.position.z * 100) / 100,
    });
  });

  return null;
}

// =============================================================================
// Animated Model Viewer Component
// =============================================================================

interface AnimatedModelViewerProps {
  modelPath: string;
  childName?: string;
  isPlaying: boolean;
  onAnimationInfo?: (info: { hasAnimation: boolean; clipCount: number }) => void;
}

function AnimatedModelViewer({
  modelPath,
  childName,
  isPlaying,
  onAnimationInfo
}: AnimatedModelViewerProps) {
  const { scene, animations } = useGLTF(modelPath);
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const actionsRef = useRef<THREE.AnimationAction[]>([]);
  const clonedSceneRef = useRef<THREE.Object3D | null>(null);

  // Clone scene and setup
  const displayScene = useMemo(() => {
    let targetScene: THREE.Object3D;

    if (childName) {
      const child = scene.getObjectByName(childName);
      if (child) {
        targetScene = child.clone();
      } else {
        targetScene = scene.clone();
      }
    } else {
      targetScene = scene.clone();
    }

    // Center and scale the object
    const box = new THREE.Box3().setFromObject(targetScene);
    const center = box.getCenter(new THREE.Vector3());
    targetScene.position.sub(center);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 1.5 / maxDim;
    targetScene.scale.setScalar(scale);

    return targetScene;
  }, [scene, childName]);

  // Update ref after render
  useEffect(() => {
    clonedSceneRef.current = displayScene;
  }, [displayScene]);

  // Setup animation mixer
  useEffect(() => {
    if (!displayScene || !animations || animations.length === 0) {
      onAnimationInfo?.({ hasAnimation: false, clipCount: 0 });
      return;
    }

    // Create mixer on the cloned scene
    const mixer = new THREE.AnimationMixer(displayScene);
    mixerRef.current = mixer;

    // Create actions for all animation clips
    const actions = animations.map(clip => {
      const action = mixer.clipAction(clip);
      action.clampWhenFinished = true;
      action.setLoop(THREE.LoopOnce, 1);
      return action;
    });
    actionsRef.current = actions;

    // Reset all actions to frame 0
    actions.forEach(action => {
      action.reset();
      action.time = 0;
      action.paused = true;
    });

    onAnimationInfo?.({ hasAnimation: true, clipCount: animations.length });

    return () => {
      mixer.stopAllAction();
      mixerRef.current = null;
      actionsRef.current = [];
    };
  }, [displayScene, animations, onAnimationInfo]);

  // Handle play state changes
  useEffect(() => {
    if (actionsRef.current.length === 0) return;

    if (isPlaying) {
      actionsRef.current.forEach(action => {
        action.reset();
        action.paused = false;
        action.play();
      });
    } else {
      actionsRef.current.forEach(action => {
        action.stop();
        action.reset();
        action.time = 0;
      });
    }
  }, [isPlaying]);

  // Update mixer each frame
  useFrame((_, delta) => {
    if (mixerRef.current && isPlaying) {
      mixerRef.current.update(delta);
    }
  });

  return <primitive object={displayScene} />;
}

// =============================================================================
// Symbol Preview Card Component
// =============================================================================

interface SymbolPreviewProps {
  symbol: SymbolDefinition;
  isSelected: boolean;
  onClick: () => void;
}

function SymbolPreview({ symbol, isSelected, onClick }: SymbolPreviewProps) {
  const { t } = useLanguage();
  const [hasAnimation, setHasAnimation] = useState(false);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  const handleAnimationInfo = useCallback((info: { hasAnimation: boolean }) => {
    setHasAnimation(info.hasAnimation);
  }, []);

  return (
    <div
      className={`relative bg-gray-800 rounded-lg overflow-hidden cursor-pointer transition-all hover:ring-2 hover:ring-purple-500 ${
        isSelected ? 'ring-2 ring-green-500' : ''
      }`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      {/* Animation indicator */}
      {hasAnimation && (
        <div className="absolute top-2 left-2 z-10 bg-purple-600 rounded-full p-1" title={t('admin.reelSymbols.hasAnimation', 'Has animation')}>
          <Film className="w-3 h-3 text-white" />
        </div>
      )}

      <div className="h-32 w-full">
        <Canvas camera={{ position: [0, 0, 3], fov: 50 }}>
          <Suspense fallback={null}>
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} intensity={1} />
            <Center>
              <AnimatedModelViewer
                modelPath={symbol.model}
                childName={symbol.modelChildName}
                isPlaying={false}
                onAnimationInfo={handleAnimationInfo}
              />
            </Center>
            <OrbitControls enableZoom={false} enablePan={false} />
            <Environment preset="studio" />
          </Suspense>
        </Canvas>
      </div>
      <div className="p-2 text-center">
        <span className="text-white text-xs font-medium truncate block">{symbol.name}</span>
        <span className="text-gray-500 text-[10px]">{symbol.type}</span>
      </div>
    </div>
  );
}

// =============================================================================
// Symbol Detail View Component
// =============================================================================

interface SymbolDetailViewProps {
  symbol: SymbolDefinition;
  onClose: () => void;
}

function SymbolDetailView({ symbol, onClose }: SymbolDetailViewProps) {
  const { t } = useLanguage();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasAnimation, setHasAnimation] = useState(false);
  const [cameraPosition, setCameraPosition] = useState({ x: 0, y: 0, z: 3 });

  const handleAnimationInfo = useCallback((info: { hasAnimation: boolean; clipCount: number }) => {
    setHasAnimation(info.hasAnimation);
  }, []);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handleReset = () => {
    setIsPlaying(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80"
        onClick={onClose}
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        aria-label={t('modal.close', 'Close')}
      />

      {/* Detail Panel */}
      <div className="relative bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-xl font-bold text-white">{symbol.name}</h3>
            <p className="text-gray-400 text-sm">{symbol.type} - {symbol.id}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            aria-label={t('modal.close', 'Close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 3D Preview */}
        <div className="h-80 w-full bg-gray-800 rounded-lg overflow-hidden mb-4">
          <Canvas camera={{ position: [0, 0, 3], fov: 50 }}>
            <Suspense fallback={null}>
              <ambientLight intensity={0.5} />
              <directionalLight position={[5, 5, 5]} intensity={1} />
              <Center>
                <AnimatedModelViewer
                  modelPath={symbol.model}
                  childName={symbol.modelChildName}
                  isPlaying={isPlaying}
                  onAnimationInfo={handleAnimationInfo}
                />
              </Center>
              <OrbitControls enableZoom={true} enablePan={true} />
              <Environment preset="studio" />
              <CameraTracker onPositionChange={setCameraPosition} />
            </Suspense>
          </Canvas>
        </div>

        {/* Controls Row */}
        <div className="flex items-center justify-between gap-4">
          {/* Animation Controls */}
          <div className="flex items-center gap-2">
            {hasAnimation ? (
              <>
                <button
                  onClick={handlePlay}
                  disabled={isPlaying}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    isPlaying
                      ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  <Play className="w-4 h-4" />
                  {t('admin.reelSymbols.playAnimation', 'Play')}
                </button>
                <button
                  onClick={handleReset}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  {t('admin.reelSymbols.resetAnimation', 'Reset')}
                </button>
              </>
            ) : (
              <span className="text-gray-500 text-sm">
                {t('admin.reelSymbols.noAnimation', 'No animation')}
              </span>
            )}
          </div>

          {/* Camera Position Display */}
          <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2">
            <Move3D className="w-4 h-4 text-gray-400" />
            <span className="text-gray-400 text-xs font-mono">
              {t('admin.reelSymbols.cameraPosition', 'x: {{x}}, y: {{y}}, z: {{z}}', {
                x: cameraPosition.x,
                y: cameraPosition.y,
                z: cameraPosition.z
              })}
            </span>
          </div>
        </div>

        {/* Config Info */}
        <div className="mt-4 bg-gray-800 rounded-lg p-3">
          <p className="text-gray-400 text-xs mb-2">
            {t('admin.reelSymbols.configInfo', 'Symbol configuration:')}
          </p>
          <pre className="text-green-400 text-xs font-mono overflow-x-auto">
{`{
  model: '${symbol.model}',${symbol.modelChildName ? `
  modelChildName: '${symbol.modelChildName}',` : ''}
  modelScale: ${symbol.modelScale ?? 1},
  modelRotation: ${JSON.stringify(symbol.modelRotation ?? { x: 0, y: 0, z: 0 })},
}`}
          </pre>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Modal Component
// =============================================================================

export default function AdminReelSymbolsModal({ onClose }: Props) {
  const { t } = useLanguage();
  const [selectedSymbol, setSelectedSymbol] = useState<SymbolDefinition | null>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (selectedSymbol) {
        setSelectedSymbol(null);
      } else {
        onClose();
      }
    }
  }, [selectedSymbol, onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="admin-symbols-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-label={t('modal.close', 'Close modal')}
      />

      {/* Modal Container */}
      <div className="relative bg-gray-900 rounded-2xl shadow-2xl max-w-6xl w-full p-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 id="admin-symbols-modal-title" className="text-xl font-bold text-white">
                {t('admin.reelSymbols.title', 'Reel Symbols Preview')}
              </h2>
              <div className="flex items-center gap-2 text-gray-400 text-sm">
                <Shield className="w-3.5 h-3.5" />
                <span>{t('admin.reelSymbols.subtitle', 'Admin only - View all slot machine symbols')}</span>
              </div>
            </div>
          </div>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg"
            aria-label={t('modal.close', 'Close modal')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Instructions */}
        <p className="text-gray-400 text-sm mb-4">
          {t('admin.reelSymbols.instructions', 'Click a symbol to view details and adjust camera. Symbols with animations show a film icon.')}
        </p>

        {/* Symbols Grid */}
        <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-4">
          {ALL_SYMBOLS.map((symbol) => (
            <SymbolPreview
              key={symbol.id}
              symbol={symbol}
              isSelected={selectedSymbol?.id === symbol.id}
              onClick={() => setSelectedSymbol(symbol)}
            />
          ))}
        </div>

        {/* Admin Badge */}
        <div className="mt-6 flex items-center justify-center gap-2 text-purple-400 text-xs">
          <Shield className="w-3.5 h-3.5" />
          <span>{t('admin.reelSymbols.adminOnly', 'This modal is only visible to administrators')}</span>
        </div>
      </div>

      {/* Detail View Overlay */}
      {selectedSymbol && (
        <SymbolDetailView
          symbol={selectedSymbol}
          onClose={() => setSelectedSymbol(null)}
        />
      )}
    </div>
  );
}
